import {
  Duration,
  DurationUnit,
  PauseAt,
  PaymentStatus,
  Subscription,
  SubscriptionStatus,
} from '@wix/ambassador-billing-v1-subscription/types';
import { addDays, addWeeks, addMonths, addYears, startOfDay, differenceInCalendarDays } from 'date-fns';

export const isRecurringSubscription = (subscription: Subscription) =>
  subscription?.billingSettings &&
  (subscription.billingSettings?.totalCycles === undefined ||
    (subscription.billingSettings?.totalCycles && subscription.billingSettings?.totalCycles > 1));

export const isPendingCancellationSubscription = (subscription: Subscription) =>
  isRecurringSubscription(subscription) && subscription.billingSettings?.cycleAutoRenew === false;

export const hasAnyDiscount = (subscription: Subscription) => {
  if (subscription.billingSettings?.discounts && subscription.billingSettings?.discounts.length > 0) {
    return true;
  }

  const hasItemWithDiscount = !!subscription.items?.find((item) => item.discounts && item.discounts.length > 0);
  return hasItemWithDiscount;
};

const isSkipPause = (subscription: Subscription) =>
  subscription.pauseInfo?.pauseAt === PauseAt.NUMBER_OF_CYCLES_FROM_NEXT_BILLING_DATE;

export const hasScheduledSkip = (subscription: Subscription) =>
  subscription.status === SubscriptionStatus.ACTIVE && isSkipPause(subscription);

export const isCurrentlySkipping = (subscription: Subscription) =>
  subscription.status === SubscriptionStatus.PAUSED && isSkipPause(subscription);

export const addCyclesFromAnchor = (anchor: Date, cycleCount: number, cycleDuration: Duration): Date => {
  const totalUnits = (cycleDuration.count ?? 1) * cycleCount;
  switch (cycleDuration.unit) {
    case DurationUnit.DAY:
      return addDays(anchor, totalUnits);
    case DurationUnit.WEEK:
      return addWeeks(anchor, totalUnits);
    case DurationUnit.MONTH:
      return addMonths(anchor, totalUnits);
    case DurationUnit.YEAR:
      return addYears(anchor, totalUnits);
    default:
      return anchor;
  }
};

export const toDateOnly = (d: Date): Date => startOfDay(d);

export const daysBetween = (from: Date, to: Date): number => differenceInCalendarDays(to, from);

export const calculateSkipCycles = (
  selectedStartDate: Date,
  selectedEndDate: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
): { pauseAtCycles: number; cyclesToSkip: number; resumeAtCycles: number } => {
  const start = toDateOnly(selectedStartDate);
  const end = toDateOnly(selectedEndDate);
  const anchor = toDateOnly(nextBillingDate);
  const maxCyclesToScan = Math.ceil(365 / durationInDays(cycleDuration)) + 2;

  let pauseAtCycles = 1;
  while (
    pauseAtCycles <= maxCyclesToScan &&
    toDateOnly(addCyclesFromAnchor(anchor, pauseAtCycles - 1, cycleDuration)) < start
  ) {
    pauseAtCycles++;
  }

  let resumeAtCycles = pauseAtCycles;
  while (
    resumeAtCycles <= pauseAtCycles + maxCyclesToScan &&
    toDateOnly(addCyclesFromAnchor(anchor, resumeAtCycles - 1, cycleDuration)) <= end
  ) {
    resumeAtCycles++;
  }

  const cyclesToSkip = Math.max(resumeAtCycles - pauseAtCycles, 1);
  return { pauseAtCycles, cyclesToSkip, resumeAtCycles: pauseAtCycles + cyclesToSkip };
};

export const isWithinSkipNoticeWindow = (
  date: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
  noticeDays: number,
): boolean => {
  if (noticeDays <= 0) {
    return false;
  }
  const d = toDateOnly(date);
  const maxCyclesToScan = Math.ceil(365 / durationInDays(cycleDuration)) + 1;
  for (let k = 0; k < maxCyclesToScan; k++) {
    const billingDate = addCyclesFromAnchor(nextBillingDate, k, cycleDuration);
    const diff = daysBetween(d, billingDate);
    if (diff < 0) {
      continue;
    }
    return diff < noticeDays;
  }
  return false;
};

export const exceedsSkipsLeft = (
  startDate: Date,
  endDate: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
  skipsLeft: number,
): boolean => calculateSkipCycles(startDate, endDate, nextBillingDate, cycleDuration).cyclesToSkip > skipsLeft;

export const toDate = (d: Date | string | null | undefined): Date | undefined =>
  d ? (typeof d === 'string' ? new Date(d) : d) : undefined;

export const getSkipCyclesCount = (subscription: Subscription): number => {
  const startCycle = subscription.pauseInfo?.startCycle ?? 0;
  const resumeCycle = subscription.pauseInfo?.resumeCycle ?? 0;
  return Math.max(resumeCycle - startCycle, 1);
};

export const getUsedSkipCount = (subscription: Subscription): number =>
  subscription.policies?.customerActionCounters?.skipCount ?? 0;

export const getSkipsLeft = (subscription: Subscription, maxUserActions: number): number =>
  Math.max(maxUserActions - getUsedSkipCount(subscription), 0);

const durationInDays = (cycleDuration: Duration): number => {
  const count = cycleDuration.count ?? 1;
  switch (cycleDuration.unit) {
    case DurationUnit.DAY:
      return count;
    case DurationUnit.WEEK:
      return count * 7;
    case DurationUnit.MONTH:
      return count * 30;
    case DurationUnit.YEAR:
      return count * 365;
    default:
      return count;
  }
};

export const isStartNoticeBeyondCycle = (subscription: Subscription, minStartNoticeDays: number): boolean => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  if (!cycleDuration) {
    return false;
  }
  return minStartNoticeDays >= durationInDays(cycleDuration);
};

export const getBillingCycleDate = (subscription: Subscription, cycle: number): Date | undefined => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  const currentCycle = subscription.billingStatus?.currentCycle;
  if (!cycleDuration || currentCycle == null) {
    return undefined;
  }
  const previousBillingDate = toDate(subscription.billingStatus?.previousBillingDate);
  if (previousBillingDate) {
    return addCyclesFromAnchor(previousBillingDate, cycle - currentCycle, cycleDuration);
  }
  const nextBillingDate = toDate(subscription.billingStatus?.nextBillingDate);
  if (nextBillingDate) {
    return addCyclesFromAnchor(nextBillingDate, cycle - (currentCycle + 1), cycleDuration);
  }
  return undefined;
};

const isWithinNoticeOf = (anchor: Date | undefined, noticeDays: number, now: Date): boolean => {
  if (noticeDays <= 0 || !anchor) {
    return false;
  }
  const diff = daysBetween(now, anchor);
  return diff >= 0 && diff < noticeDays;
};

export const isWithinRevocationNotice = (subscription: Subscription, noticeDays: number, now: Date): boolean =>
  isWithinNoticeOf(
    hasScheduledSkip(subscription)
      ? getBillingCycleDate(subscription, subscription.pauseInfo?.startCycle ?? 0)
      : toDate(subscription.billingStatus?.nextBillingDate),
    noticeDays,
    now,
  );

export const isNextBillingWithinNotice = (subscription: Subscription, noticeDays: number, now: Date): boolean =>
  isWithinNoticeOf(toDate(subscription.billingStatus?.nextBillingDate), noticeDays, now);

export const getLastCycleDate = (subscription: Subscription): Date | undefined => {
  const totalCycles = subscription.billingSettings?.totalCycles;
  if (!totalCycles) {
    return undefined;
  }
  return getBillingCycleDate(subscription, totalCycles);
};

export const hasSkippableCycle = (subscription: Subscription): boolean => {
  const nextBillingDate = toDate(subscription.billingStatus?.nextBillingDate);
  if (!nextBillingDate) {
    return false;
  }
  const lastCycleDate = getLastCycleDate(subscription);
  return !lastCycleDate || toDateOnly(nextBillingDate) <= toDateOnly(lastCycleDate);
};

export const isInFreeTrial = (subscription: Subscription): boolean => {
  if (subscription.status !== SubscriptionStatus.ACTIVE || !subscription.billingSettings?.freeTrialDuration) {
    return false;
  }
  const freeTrialEnd = toDate(subscription.billingStatus?.freeTrialData?.endDate);
  return !!freeTrialEnd && freeTrialEnd.getTime() > new Date().getTime();
};

export type UnskipModalVariant = 'standard' | 'partial';

export const shouldShowUnskipCta = (subscription: Subscription, noticeDays: number, now: Date): boolean => {
  const scheduled = hasScheduledSkip(subscription);
  const skipping = isCurrentlySkipping(subscription);
  if (!scheduled && !skipping) {
    return false;
  }
  const cycles = getSkipCyclesCount(subscription);
  const withinNotice = isWithinRevocationNotice(subscription, noticeDays, now);
  return cycles > 1 || !withinNotice;
};

export const shouldShowSkipCta = (subscription: Subscription, minStartNoticeDays: number): boolean =>
  subscription.status === SubscriptionStatus.ACTIVE &&
  !hasScheduledSkip(subscription) &&
  !isCurrentlySkipping(subscription) &&
  !isStartNoticeBeyondCycle(subscription, minStartNoticeDays) &&
  !isInFreeTrial(subscription) &&
  !isInGracePeriod(subscription) &&
  hasSkippableCycle(subscription);

export const getUnskipModalVariant = (subscription: Subscription, noticeDays: number, now: Date): UnskipModalVariant =>
  getSkipCyclesCount(subscription) > 1 && isWithinRevocationNotice(subscription, noticeDays, now)
    ? 'partial'
    : 'standard';

export const isInGracePeriod = (subscription: Subscription | undefined) => {
  return !!(
    subscription &&
    subscription.billingStatus?.gracePeriodData &&
    subscription.billingStatus?.latestPaymentData?.paymentStatus === PaymentStatus.FAILED &&
    subscription.status !== SubscriptionStatus.CANCELED
  );
};
